<script setup>
import { gameTypeLabel } from '../services/game-label'
import { reportBoundary, dayStart, dayEnd } from '../../../shared/business-day.js'
/**
 * 牌九「下注记录」弹窗，逐条对齐旧版 web/19.0d8589924b85d555a0a0.js 里的
 * app-bet 组件（类 f、模板工厂 pn + 子模板 un/on/an/rn/cn/sn/bn/gn、样式 en）。
 *
 * 旧版打开点：右侧菜单「下注」→ betModal()
 *   modalCtrl.create({ component: f, componentProps: { game } })
 * 账单统计里点「盈利」列也会打开它，那时会额外传 start / end。
 *
 * 旧版构造函数：
 *   this.lotteryState = '-1'
 *   this.paramShow = true
 *   this.startTime = moment(navParams.get('start')).format('YYYY-MM-DDT00:00')
 *   this.endTime   = moment(navParams.get('end')).format('YYYY-MM-DDT23:59')
 * betModal() 没传 start/end，moment(undefined) 就是当前时间，
 * 所以从菜单进来默认查的是「今天 00:00 ~ 今天 23:59」。
 *
 * ngOnInit → getBetLists()
 *   getBetList({game, lottery, page, start, end}) → POST /game/bet/lists
 *   成功：page = t.page；lists = page_index===1 ? t.data.lists : lists.concat(t.data.lists)
 */
import { computed, onMounted, ref } from 'vue'
import { request } from '../services/api'
import PullRefresher from './PullRefresher.vue'
import LegacySelect from './LegacySelect.vue'
import LegacyAlert from './LegacyAlert.vue'

const props = defineProps({
  // 旧版 navParams.get('game')
  game: { type: Object, required: true },
  // 旧版 navParams.get('start') / get('end')，菜单入口不传
  start: { type: String, default: '' },
  end: { type: String, default: '' },
})
const emit = defineEmits(['close'])

// 旧版 paramShow 默认 true，点「筛选」标题切换
const paramShow = ref(true)
/**
 * 旧版 lotteryState 初值是 '-1'（不带空格），而三个 ion-select-option 的 value
 * 在模板里写成 "-1 " / "1 " / "0 "（都带尾空格），两边匹配不上，
 * 所以旧版刚打开时 select 显示的是空 placeholder、只剩一个小箭头。
 * 这里初值对齐成 '-1 '，让它正常显示「不限」；传给后端的值不变（后端按数字比较）。
 */
const lotteryState = ref('-1 ')
const lists = ref([])
// 后端 Api::page() 结构
const page = ref({ item_total: 0, page_index: 1, page_size: 15, page_count: 1 })
const loading = ref(false)
const notice = ref('')
// 旧版 cancelAll 前的 alertCtrl 确认框
const confirmTarget = ref(null)

/** 旧版 moment(v).format('YYYY-MM-DDT00:00')；v 为空时 moment() 取当前时间 */
function toLocalIso(value, timePart) {
  // 账单统计传进来的是后端的 'Y-m-d H:i:s'，把空格换成 T 才能被 Safari 正确解析
  const date = value ? new Date(String(value).replace(' ', 'T')) : new Date()
  const base = Number.isNaN(date.getTime()) ? new Date() : date
  const pad = (number) => String(number).padStart(2, '0')
  return `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}T${timePart}`
}
const startTime = ref(reportBoundary(props.start) || dayStart())
const endTime = ref(reportBoundary(props.end, true) || dayEnd())

const gameId = computed(() => props.game?.game_id)
const hasMore = computed(() => page.value.page_count > 1 && page.value.page_count != page.value.page_index)

// 旧版「结算状态」的三个 ion-select-option，value 末尾都带一个空格，照抄
const lotteryOptions = [
  { value: '-1 ', label: '不限' },
  { value: '1 ', label: '已结算' },
  { value: '0 ', label: '未结算' },
]

// 旧版 DecimalPipe '.2-2'
const decimal2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
function fmt2(value) { return decimal2.format(Number(value || 0)) }

/** 旧版 [ngClass]="{win: x>0, lost: x<0}" */
function signClass(value) { return { win: Number(value) > 0, lost: Number(value) < 0 } }

/**
 * 旧版 {{ bet_create_time | amDateFormat:'MM-DD HH:mm' }}
 * 后端给的可能是秒级时间戳，也可能是 'Y-m-d H:i:s' 字符串，
 * 这里跟 BetRecordsPage.vue 里已有的处理保持一致。
 */
function fmtTime(value) {
  if (!value) return ''
  const date = new Date(typeof value === 'number' || /^\d+$/.test(String(value)) ? Number(value) * 1000 : value)
  if (Number.isNaN(date.getTime())) return String(value)
  const pad = (number) => String(number).padStart(2, '0')
  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** 旧版 getBetLists(l=null)，l 是 refresher / infinite 事件 */
async function getBetLists(event = null) {
  if (!gameId.value) { event?.complete(); return }
  loading.value = true
  try {
    const response = await request('/game/bet/lists', {
      method: 'POST',
      data: {
        game: gameId.value,
        lottery: lotteryState.value,
        page: page.value.page_index,
        start: startTime.value,
        end: endTime.value,
      },
    })
    page.value = { ...page.value, ...(response.page || {}) }
    const incoming = response.data?.lists || []
    lists.value = page.value.page_index === 1 ? incoming : [...lists.value, ...incoming]
  } catch (error) {
    notice.value = error.message || '加载失败'
  } finally {
    loading.value = false
    event?.complete()
  }
}

/** 旧版 changParam()：页码归 1 后重查 */
function changParam() {
  page.value = { ...page.value, page_index: 1 }
  void getBetLists()
}

/** 旧版 doRefresh(l) */
function doRefresh(event) {
  page.value = { ...page.value, page_index: 1 }
  void getBetLists(event)
}

/** 旧版 doInfinite(l)：超过最后一页时 toast 提示 */
function doInfinite() {
  const next = page.value.page_index + 1
  if (next > page.value.page_count) { notice.value = '已经最后一页了'; return }
  page.value = { ...page.value, page_index: next }
  void getBetLists()
}

/**
 * 旧版明细行的「取消」按钮走的是 cancelAll($implicit, i)：
 *   alertCtrl.create({header:'确认', message:'确定取消该注单吗？', buttons:[取消, 确认]})
 *   确认后 cancelAllBet(row.game_id, row.game_no, row.play_id) → POST /game/bet/cancel
 *   成功则把该行从列表里 splice 掉并 toast「取消成功」
 */
function askCancel(row, index) { confirmTarget.value = { row, index } }

async function doCancel() {
  const target = confirmTarget.value
  confirmTarget.value = null
  if (!target) return
  const { row, index } = target
  try {
    await request('/game/bet/cancel', {
      method: 'POST',
      data: { game: row.game_id, game_no: row.game_no, play: row.play_id },
    })
    if (index >= 0) lists.value.splice(index, 1)
    notice.value = '取消成功'
  } catch (error) {
    notice.value = error.message || '取消失败'
  }
}

// 旧版 ngOnInit
onMounted(() => { void getBetLists() })
</script>

<template>
  <div class="bet-modal">
    <!-- 旧版 ion-header > ion-toolbar：arrow-back + 标题「下注记录」 -->
    <header class="modal-header">
      <button class="modal-back" type="button" aria-label="返回" @click="emit('close')">
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </button>
      <h1 class="modal-title">下注记录<span v-if="gameTypeLabel(game)"> · {{ gameTypeLabel(game) }}</span></h1>
      <span class="modal-spacer" aria-hidden="true"></span>
    </header>

    <!-- 旧版 ion-refresher，参数与账单统计一致 -->
    <PullRefresher
      pulling-text="下拉刷新"
      refreshing-text="下拉刷新..."
      refreshing-spinner="circles"
      @refresh="doRefresh"
    >
      <!-- 旧版第一个 ion-list：筛选 -->
      <section class="ion-list">
        <!-- 旧版 ion-list-header (click)="paramShow = !paramShow"，
             右侧 *ngIf 切换 arrow-down / arrow-up 两个 ion-icon。
             旧版 name 写成 "arrow-down "（尾空格），ionicons 拼出的 svg 路径 404，
             所以线上其实看不到箭头；这里用正确的图标画出来。 -->
        <div class="list-header lists-header" @click="paramShow = !paramShow">
          <span> 筛选 </span>
          <svg v-if="paramShow" class="header-arrow" viewBox="0 0 512 512" aria-hidden="true"><path d="M256 392L100 236l28-28 128 128 128-128 28 28z" fill="currentColor" /></svg>
          <svg v-else class="header-arrow" viewBox="0 0 512 512" aria-hidden="true"><path d="M256 120l156 156-28 28-128-128-128 128-28-28z" fill="currentColor" /></svg>
        </div>

        <!-- 旧版 an：*ngIf="paramShow" 的结算状态行 -->
        <div v-if="paramShow" class="list-item">
          <div class="list-item-inner">
            <div class="ion-label">结算状态:</div>
            <LegacySelect
              v-model="lotteryState"
              :options="lotteryOptions"
              alert-header="结算状态:"
              cancel-text="取消"
              ok-text="确定"
              @change="changParam"
            />
          </div>
        </div>
      </section>

      <!-- 旧版 bn：*ngIf="lists" 的明细 -->
      <section v-if="lists.length" class="ion-list">
        <div class="list-header"> 明细 </div>
        <div v-for="(row, index) in lists" :key="row.bet_id ?? index" class="list-item">
          <div class="list-item-inner">
            <!-- 旧版 rn：div.bet-list-item 三等分 -->
            <div class="bet-list-item">
              <div class="game-info">
                <div class="game-no"> {{ row.game_no }} </div>
                <div class="bet-time"> {{ fmtTime(row.bet_create_time) }} </div>
              </div>
              <div class="bet-info">
                <div class="info-item"><span class="info-title"> 玩法: </span><span class="info-value"> {{ row.play_title }} </span></div>
                <div class="info-item"><span class="info-title"> 赔率: </span><span class="info-value"> {{ row.bet_odds }} </span></div>
                <div class="info-item"><span class="info-title"> 金额: </span><span class="info-value"> {{ fmt2(row.bet_money) }} </span></div>
              </div>
              <div class="bet-action">
                <!-- 旧版 sn：*ngIf="bet_lottery_state == 0" -->
                <div v-if="row.bet_lottery_state == 0" class="info-item">
                  <button class="ion-button button-small button-solid ion-color-danger" type="button" @click="askCancel(row, index)">
                    <span class="button-inner">取消</span>
                  </button>
                </div>
                <!-- 旧版 cn：*ngIf="bet_lottery_state == 1" -->
                <template v-else-if="row.bet_lottery_state == 1">
                  <div class="info-item">
                    <span class="info-title"> 中奖: </span>
                    <span class="info-value" :class="signClass(row.bet_money_win)"> {{ fmt2(row.bet_money_win) }} </span>
                  </div>
                  <div class="info-item">
                    <span class="info-title"> 反水: </span>
                    <span class="info-value"> {{ fmt2(row.bet_money_back) }} </span>
                  </div>
                  <div class="info-item">
                    <span class="info-title"> 盈利: </span>
                    <span class="info-value" :class="signClass(row.bet_money_pl)"> {{ fmt2(row.bet_money_pl) }} </span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 旧版 gn：ion-infinite-scroll，ngIf 是 page_count>1 && page_count!=page_index -->
      <!-- 旧版 ion-infinite-scroll-content loadingSpinner="bubbles" loadingText="正在加载"，
           滚动到底自动触发；这里没有 Ionic，跟统计弹窗一样退化成按钮 -->
      <button v-if="hasMore" class="load-more" type="button" :disabled="loading" @click="doInfinite">
        正在加载
      </button>
      <p v-if="notice" class="modal-notice">{{ notice }}</p>
    </PullRefresher>

    <!-- 旧版 cancelAll 的确认 alert -->
    <Teleport to="body">
      <LegacyAlert
        v-if="confirmTarget"
        header="确认"
        message="确定取消该注单吗？"
        cancel-text="取消"
        ok-text="确认"
        @cancel="confirmTarget = null"
        @confirm="doCancel"
      />
    </Teleport>
  </div>
</template>

<style scoped>
/* 弹窗外壳与顶栏跟其它牌九弹窗一致；顶栏色取旧版主题变量 #f95454 */
.bet-modal{position:fixed;z-index:30;top:0;left:0;width:100%;height:100%;background:#fff;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif}
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;font-size:22px;line-height:1;color:inherit;cursor:pointer;padding:0}
.modal-back .modal-icon{width:24px;height:24px;display:block}
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:inherit}
.modal-spacer{width:56px;flex:none}

/* --- Ionic 4 md 的真实默认值，取自 web/135.773ae5d979ae5c36fbef.js --- */
/* ion-list（.list-md） */
.ion-list{display:block;margin:0;padding:8px 0;background:#fff;list-style-type:none}
/* ion-list-header（md）：min-height 45px、padding-left 16px、font-size 14px、color #000 */
.list-header{display:flex;align-items:center;justify-content:space-between;width:100%;margin:0;padding:0 16px 0 16px;min-height:45px;font-size:14px;background:transparent;color:#000;overflow:hidden}
/* 旧版样式表里有 .lists-header{justify-content:flex-start}，但模板没给 ion-list-header 加这个类，
   实际生效的是 ion-list-header 默认的 space-between（所以箭头在最右） */
.list-header .header-arrow{width:1em;height:1em;flex:none;opacity:.54}
/* ion-item（md）：外层 .item-native + 内层 .item-inner 两层 */
.list-item{display:flex;align-items:center;justify-content:space-between;width:100%;min-height:48px;margin:0;padding:0 0 0 16px;background:#fff;color:#000;font-size:.8rem;overflow:hidden;position:relative;box-sizing:border-box}
/* justify-content 跟着 ion-item 的 .item-native 走 space-between，把 select 推到右端 */
.list-item-inner{display:flex;flex:1;flex-direction:inherit;align-items:inherit;justify-content:space-between;align-self:stretch;min-height:inherit;padding:0 16px 0 0;border-width:0 0 1px 0;border-style:solid;border-color:rgba(0,0,0,.13);overflow:hidden;box-sizing:border-box}
/* ion-label（md）本身不带 flex，宽度按内容走 */
.ion-label{display:block;font-size:inherit;text-overflow:ellipsis;white-space:nowrap;overflow:hidden}

/* --- 以下逐条照抄旧版 app-bet 的样式表 en（chunk 19），:host → .bet-modal --- */
.bet-modal{background:#fff}
.lists-header{justify-content:flex-start}
.ion-list .info-title{color:#666;display:inline-block;margin-right:10px}
.ion-list .info-value{display:inline-block}
.ion-list .win{color:red}
.ion-list .lost{color:green}
.ion-list .bet-list-item{padding-top:5px;padding-bottom:5px;width:100%;font-size:.8rem;display:flex;align-items:center;justify-content:space-between}
.ion-list .bet-list-item .game-info{width:33%}
.ion-list .bet-list-item .bet-info{width:33%}
.ion-list .bet-list-item .bet-action{width:33%}

/* ion-button color="danger" size="small"，取自 web/75.fd98cb3f8492a7c96e1e.js（md）
   host: margin 4px 2px / height 36px / font-size 14px / font-weight 500 / letter-spacing .06em / uppercase
   .button-small: padding 0 .9em / height 2.1em / font-size 13px
   .button-solid 的阴影是 0 3px 1px -2px .2, 0 2px 2px 0 .14, 0 1px 5px 0 .12
   ion-color-danger → --ion-color-base:#f04141 / contrast:#fff（注意 danger 跟顶栏主题色不是同一个红） */
.ion-button{
  display:inline-flex;align-items:center;justify-content:center;
  margin:4px 2px;border:0;border-radius:4px;
  font-weight:500;letter-spacing:.06em;text-transform:uppercase;cursor:pointer;
  height:2.1em;font-size:13px;padding:0 .9em;
  background:var(--ion-color-danger,#f04141);color:#fff;
  box-shadow:0 3px 1px -2px rgb(0 0 0 / 20%),0 2px 2px 0 rgb(0 0 0 / 14%),0 1px 5px 0 rgb(0 0 0 / 12%);
}
.ion-button .button-inner{display:flex;flex-flow:row nowrap;flex-shrink:0;align-items:center;justify-content:center;width:100%;height:100%}

.load-more{display:block;width:calc(100% - 32px);margin:10px 16px;padding:8px 0;border:1px solid #ddd;border-radius:4px;background:#fafafa;font-size:.8rem}
.modal-notice{margin:10px 16px;color:#999;font-size:.8rem;text-align:center}
</style>
